import { PropsWithChildren } from 'react';

import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import Link from 'next/link';
import { Breadcrumb, Col, Container, Pagination, Row } from 'react-bootstrap';
import { Rating } from 'react-simple-star-rating';

import ContentfulContent from '@website/components/Contentful/ContentfulContent';
import HeaderSection from '@website/components/HeaderSection';
import { algoliaSearchReviews } from '@website/components/LandingPages/algoliaSearch';
import DefaultLayout from '@website/components/layouts/DefaultLayout';
import SeoMetaTags from '@website/components/seoMetaTags';
import { ContentFulPage } from '@website/components/StaticPageContent/StaticPageContent';
import CardReview from '@website/components/trips/TripsPage/reviews/CardReview';
import TrustPilotWidget from '@website/components/trustpilot/TrustPilotWidget';
import { ssrClient } from '@website/libs/graphql/apollo';
import {
  AllReviewStatsDocument,
  AllReviewStatsQuery,
  FetchGuestDetailsQuery,
  Review as ReviewItem,
} from '@website/types/graphqlOperations';
import { aboutUsPageQuery } from './[page]';
import { contentFulClient } from './blog/[postSlug]';

type Review = {
  objectID: string;
  guest: Omit<FetchGuestDetailsQuery['guest'], 'review'>;
} & ReviewItem;

export async function getServerSideProps({
  query,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<AllReviewsPageProps>
> {
  const page = parseInt(query.page as string) - 1 || 0;
  const reviewsResponse = await algoliaSearchReviews(page);

  const nbPages = reviewsResponse?.results[0]['nbPages'];
  const pageList = Array.from(Array(nbPages).keys());

  const client = ssrClient();

  const reviewStatsResponse = await client.query({
    query: AllReviewStatsDocument,
  });

  const res = await contentFulClient.query({
    query: aboutUsPageQuery,
    variables: {
      pageSlug: 'our-reviews',
    },
    fetchPolicy: 'no-cache',
  });

  const pageContent = (await res.data.pagesCollection
    ?.items?.[0]) as ContentFulPage;

  return {
    props: {
      resultsState: reviewsResponse?.results[0]['hits'] as Review[],
      stats: reviewStatsResponse.data.reviewStatistics,
      page: pageContent,
      pageList,
      pageNumber: page,
    },
    // revalidate every 2 hours
    // revalidate: 7200,
  };
}

type AllReviewsPageProps = {
  resultsState: Review[];
  stats: AllReviewStatsQuery['reviewStatistics'];
  page: ContentFulPage;
  pageList: number[];
  pageNumber: number;
};

export default function AllReviewsPage({
  resultsState,
  stats,
  page,
  pageList,
  pageNumber,
}: AllReviewsPageProps) {
  const totalReviewsNearestHundred = (
    Math.ceil(stats.reviewCount / 100) * 100
  ).toLocaleString();

  return (
    <>
      <SeoMetaTags
        title={page.metaTitle}
        description={page.description}
        showWebsiteName={false}
      />

      <HeaderSection title={page.title} image={page.image.url} />

      <section>
        <Container>
          <Row>
            <Col xs={12} sm={8} md={7} className="mx-auto pt-2">
              <Breadcrumb className="py-2">
                <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                <Breadcrumb.Item active>{page.title}</Breadcrumb.Item>
              </Breadcrumb>
              <p className="lead">{page.description}</p>
            </Col>
          </Row>
        </Container>
      </section>

      <section>
        <Container>
          <ContentfulContent document={page} />
        </Container>
      </section>

      <Container fluid className="py-3 pt-5 mt-4 bg-light">
        <Col xs={12} md={8} className="text-center mx-auto">
          <h2>
            Rated excellent by over {totalReviewsNearestHundred} travelers
          </h2>
        </Col>
        <Col
          xs={12}
          sm={8}
          md={7}
          className="text-center fw-normal mt-3 d-flex mx-auto justify-content-center"
        >
          <Rating
            initialValue={5}
            readonly
            size={25}
            allowFraction
            fillColor="#4e66f8"
          />
          <span className="ms-2 mt-1">
            4.9/5 ({stats.reviewCount.toLocaleString()})
          </span>
        </Col>
        <Col xs={12} sm={8} md={7} className="text-center fw-normal mx-auto">
          <p className="mt-4 mb-0">Also reviewed on ...</p>
          <p>
            <TrustPilotWidget
              templateId="53aa8807dec7e10d38f59f32"
              height="150px"
            />
          </p>
        </Col>
      </Container>

      <Container className="mb-5 mt-3 px-lg-5">
        <Col xs={12} md={8} className="mx-auto">
          {resultsState.map((review) => (
            <CardReview
              key={review.id}
              review={{
                ...review,
                user: review.guest.user,
              }}
              trip={review.guest.booking.group.trip}
              host={review.guest.booking.group.trip.host}
            />
          ))}
        </Col>
        <Col xs={12} md={8} className="text-center mt-4">
          {pageList.length > 1 && (
            <Pagination className="justify-content-center">
              {pageList
                // .map((value) => value + 1)
                .map((value) => {
                  return (
                    <Link
                      key={value}
                      href={`/reviews?page=${value + 1}`}
                      passHref
                      legacyBehavior
                    >
                      <Pagination.Item active={value === pageNumber}>
                        {value + 1}
                      </Pagination.Item>
                    </Link>
                  );
                })}
            </Pagination>
          )}
        </Col>
      </Container>
    </>
  );
}

AllReviewsPage.Layout = function Layout({ children }: PropsWithChildren<{}>) {
  return <DefaultLayout className="">{children}</DefaultLayout>;
};
